import React from 'react';
import {
  CloudArrowDownIcon,
  LockOpenIcon,
  FolderIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { ModalShell, ModalActions } from './modals/core';
import { DownloadProgress } from '../hooks/useDownloadProgress';

interface DownloadProgressModalProps {
  visible: boolean;
  progress: DownloadProgress;
  onCancel: () => void;
  onClose: () => void;
}

const DownloadProgressModal: React.FC<DownloadProgressModalProps> = ({
  visible,
  progress,
  onCancel,
  onClose,
}) => {
  const getStepIcon = () => {
    if (progress.error) {
      return <ExclamationTriangleIcon className="h-8 w-8 text-error-500" />;
    }

    if (!progress.active) {
      return <CheckCircleIcon className="h-8 w-8 text-success-500" />;
    }

    switch (progress.step) {
      case 'querying':
        return <CloudArrowDownIcon className="h-8 w-8 text-info-500 animate-pulse" />;
      case 'downloading':
        return <LockOpenIcon className="h-8 w-8 text-warning-500 animate-pulse" />;
      case 'zipping':
        return <FolderIcon className="h-8 w-8 text-success-500 animate-pulse" />;
      default:
        return <CloudArrowDownIcon className="h-8 w-8 text-info-500 animate-pulse" />;
    }
  };

  const getProgressPercentage = () => {
    if (!progress.total || !progress.current) return 0;
    return Math.round((progress.current / progress.total) * 100);
  };



  const getStepTitle = () => {
    if (progress.error) {
      return 'Fehler aufgetreten';
    }

    if (!progress.active) {
      return 'Download abgeschlossen';
    }

    switch (progress.step) {
      case 'querying':
        return 'Dokumente werden gesucht...';
      case 'downloading':
        return 'Dokumente werden heruntergeladen...';
      case 'zipping':
        return 'ZIP-Archiv wird erstellt...';
      default:
        return 'Download läuft...';
    }
  };

  // Nicht-interaktiver Fortschritt: Schließen nur über die expliziten Buttons
  // (X-Button ausgeblendet solange aktiv, kein Backdrop-/Escape-Close).
  const handleClose = () => {
    onClose();
  };

  const footer = progress.active ? (
    <ModalActions danger primary={{ label: 'Abbrechen', onClick: onCancel }} />
  ) : progress.error ? (
    <ModalActions secondary={{ label: 'Schließen', onClick: onClose }} />
  ) : (
    <ModalActions primary={{ label: '✓ Fertig', onClick: onClose }} />
  );

  return (
    <ModalShell
      open={visible}
      onClose={handleClose}
      variant="progress"
      size="sm"
      title="Dokumente herunterladen"
      hideCloseButton={progress.active}
      closeOnBackdrop={false}
      closeOnEscape={false}
      footer={footer}
    >
      {/* Main Content */}
      <div className="flex flex-col items-center gap-3 sm:gap-4">
        {/* Icon */}
        <div className="flex items-center justify-center">
          {getStepIcon()}
        </div>

        {/* Title and Message */}
        <div className="text-center">
          <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
            {getStepTitle()}
          </h4>

          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
            {progress.error ? progress.error : progress.message}
          </p>

          {/* Document Name */}
          {progress.documentName && progress.active && (
            <div className="w-full max-w-full">
              <p
                className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 sm:px-3 py-2 rounded-lg font-mono text-center truncate"
                title={progress.documentName}
                style={{ minHeight: '2rem' }}
              >
                {progress.documentName}
              </p>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {progress.active && progress.total && progress.current !== undefined && (
          <div className="w-full">
            <div className="flex justify-between items-center mb-2 text-xs sm:text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                {progress.current} von {progress.total}
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                {getProgressPercentage()}%
              </span>
            </div>

            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-primary-600 dark:bg-primary-500 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>
        )}

        {/* Loading Spinner (when no specific progress) */}
        {progress.active && (!progress.total || progress.current === undefined) && (
          <div className="w-full flex justify-center">
            <svg
              className="animate-spin h-5 w-5 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-500"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z"
              />
            </svg>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default DownloadProgressModal;
